import {
  useGetAllStoresByIds,
  useGetSimplifiedPermissionsPerStoreQueries,
  useGetSimplifiedPermissionsQueries,
} from './useGetEmployeeSimplePermissions';

import { PermissaoDescricao, PermissaoDeGrupoDescricao, PermissaoSimplificadaType, Maybe } from 'types/graphql';

export function useGetPermissions() {
  const simplifiedPermissions = useGetSimplifiedPermissionsQueries();
  return [
    (simplifiedPermissions?.data?.permissoesSimplificadas?.agrupadoPorDescricao?.map(d => d?.permssaoDescricao) ||
      []) as Array<PermissaoDescricao | PermissaoDeGrupoDescricao>,
    {
      loading: simplifiedPermissions?.loading,
      error: simplifiedPermissions.error,
    },
  ] as const;
}

export function useHasPermission(perms: PermissaoDescricao[]) {
  const [permissions] = useGetPermissions();
  const allowed = perms.filter(perm => permissions.includes(perm));
  return allowed.length > 0 && allowed;
}

export function useHasPermissionCallBack() {
  const [permissions] = useGetPermissions();
  return (perms: PermissaoDescricao[]) => perms?.filter(perm => permissions?.includes(perm))?.length > 0;
}

const getEntityIdsByPermission = (
  permission: PermissaoDescricao | PermissaoDeGrupoDescricao,
  simplifiedPermissions?: Maybe<PermissaoSimplificadaType>,
) => {
  return (simplifiedPermissions?.agrupadoPorDescricao || [])
    .filter(desc => desc?.permssaoDescricao === permission)
    .reduce<number[]>((entityIds, desc) => {
      return entityIds.concat((desc?.loja || []).filter((entityId): entityId is number => entityId != null));
    }, []);
};

export const checkPermission = (
  permission: PermissaoDescricao | PermissaoDeGrupoDescricao,
  entityId?: number | string | null,
  simplifiedPermissions?: Maybe<PermissaoSimplificadaType>,
) => {
  return getEntityIdsByPermission(permission, simplifiedPermissions).includes(Number(entityId));
};

export const checkPermissionInAllEntities = (
  permission: PermissaoDescricao | PermissaoDeGrupoDescricao,
  entityScopePermission: PermissaoDescricao | PermissaoDeGrupoDescricao,
  simplifiedPermissions?: Maybe<PermissaoSimplificadaType>,
) => {
  const entityScope = getEntityIdsByPermission(entityScopePermission, simplifiedPermissions);

  if (!entityScope.length) return false;

  const allowedEntities = getEntityIdsByPermission(permission, simplifiedPermissions);

  return entityScope.every(entityId => allowedEntities.includes(entityId));
};

export function useHasPermissionInEntity(
  permission: PermissaoDescricao | PermissaoDeGrupoDescricao,
  entityId?: number | string | null,
) {
  const simplifiedPermissions = useGetSimplifiedPermissionsQueries();
  return checkPermission(permission, entityId, simplifiedPermissions?.data?.permissoesSimplificadas);
}

export function useHasPermissionInAllEntities(
  permission: PermissaoDescricao | PermissaoDeGrupoDescricao,
  entityScopePermission: PermissaoDescricao | PermissaoDeGrupoDescricao,
) {
  const simplifiedPermissions = useGetSimplifiedPermissionsQueries();
  return checkPermissionInAllEntities(
    permission,
    entityScopePermission,
    simplifiedPermissions?.data?.permissoesSimplificadas,
  );
}

export function useHasPermissionInEntityCallBack(id?: number | string | null) {
  const simplifiedPermissions = useGetSimplifiedPermissionsQueries();
  return (
    permission: PermissaoDescricao | PermissaoDeGrupoDescricao,
    entityId: number | string | null | undefined = id,
  ) => [
    (
      simplifiedPermissions?.data?.permissoesSimplificadas?.agrupadoPorDescricao?.filter(desc => {
        return desc?.permssaoDescricao === permission && desc?.loja?.includes(Number(entityId));
      }) || []
    ).length > 0,
  ];
}

export function useEmployeeEntitysByPermission(
  permission: PermissaoDescricao | PermissaoDeGrupoDescricao,
  _withGoals = false,
) {
  const simplifiedPermissionsPerStores = useGetSimplifiedPermissionsPerStoreQueries();
  const storesIds = simplifiedPermissionsPerStores?.data?.permissoesSimplificadas?.agrupadoPorLoja
    ?.filter(f => f?.permssaoDescricao?.includes(permission))
    ?.map(groupedStore => groupedStore?.loja)
    .join(',');
  const stores = useGetAllStoresByIds(!storesIds, { id_In: storesIds });
  const formattedStores = stores.data?.todasEntidadesRelay?.edges?.map(p => p?.node);

  return {
    stores: formattedStores,
    storesLoading: stores?.loading,
    storeError: stores.error,
  };
}

export function useEmployeeEntitysByPermissions(permissions: (PermissaoDescricao | PermissaoDeGrupoDescricao)[]) {
  const simplifiedPermissionsPerStores = useGetSimplifiedPermissionsPerStoreQueries();
  const storesIds = simplifiedPermissionsPerStores?.data?.permissoesSimplificadas?.agrupadoPorLoja
    ?.filter(f => {
      const verifyCommonDescriptions =
        f?.permssaoDescricao?.filter(desc => permissions?.includes(desc as PermissaoDescricao)) || [];
      return verifyCommonDescriptions.length > 0;
    })
    ?.map(groupedStore => groupedStore?.loja)
    .join(',');
  const storesQuery = useGetAllStoresByIds(!storesIds, { id_In: storesIds });
  const stores = storesQuery?.data?.todasEntidadesRelay?.edges?.map(edge => edge?.node);
  return {
    stores,
    storesLoading: storesQuery.loading,
    storeError: storesQuery.error,
  };
}
